import React, { useEffect, useRef, useState, useContext } from 'react';
import enUS from 'antd/es/locale/en_US';
import zhCN from 'antd/es/locale/zh_CN';
import zhHk from 'antd/es/locale/zh_HK';
import moment from 'moment';
import Cookie from 'js-cookie';
import 'moment/locale/zh-cn';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Layout, Menu, ConfigProvider, Popover, Badge } from 'antd';

import HeadPortraitSelect from '@src/Layouts/MainLayout/component/HeadPortraitSelect';
import RouterViews from '@src/Layouts/MainLayout/component/RouterViews';
import { TimeTranslateCom } from '@src/components/TimeTranslateCom';

import { UserInfoStore } from '@src/store/UserInfoData/Provider';
import { leftMenu, mainRoutes } from '@src/routes/mainRoutes';
import { getCompanyInfo, switchLangServer } from '@src/services/userInfo';
import { getUserInfo } from '@src/services/accountSettings';
import { getOperatorInfo } from '@src/services/companyInformation';
import { cryptoDecrypt } from '@src/utils/encryption';
import { getMessageList, messageRead } from '@src/services/messageNotify';
import messageCom from '@src/utils/messageCom';
import { resetCookie } from '@src/utils/pendingInitInfo';

import styles from './styles.module.less';

const MainLayout = () => {
  moment.locale('en');
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = sessionStorage.getItem('oper_lang') || 'en_US';
  const rootSubmenuKeysRef = useRef([]);
  const permissionsArrayRef = useRef(JSON.parse(localStorage.getItem('permissions'))).current;
  const infoNotifyTime = useRef(null);
  const notifyRequestFlag = useRef(false);
  const logoutTimerRef = useRef(null);

  const { state, dispatch } = useContext(UserInfoStore);
  const [locale, setLocal] = useState(enUS);
  const [menuDefaultSelected, setMenuDefaultSelected] = useState(['']);
  const [menuDefaultOpen, setMenuDefaultOpen] = useState(['']);
  const [pageTitle, setPageTitle] = useState('');
  const [leftMenuList, setLeftMenuList] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [infoNotifyList, setInfoNotifyList] = useState([]);

  // 组件国际化
  useEffect(() => {
    i18n.changeLanguage(state.userInfo.language);
    switch (state.userInfo.language) {
      case 'en_US':
        return setLocal(enUS);
      case 'zh_CN':
        return setLocal(zhCN);
      // case 'zh_XG':
      //   return setLocal(zhHk);
      default:
        return setLocal(enUS);
    }
  }, [state]);

  // 切换翻译
  const onLangChange = (lang) => {
    i18n.changeLanguage(lang);
    sessionStorage.setItem('oper_lang', lang);
    switchLangServer(lang)

    dispatch({
      type: 'SET_USER_INFO',
      payload: {
        ...state.userInfo,
        language: lang
      }
    });
  };

  useEffect(() => {
    onLoad();
  }, []);


  // 请求公司信息
  const onLoad = async () => {
    onCompanyLogoLoad();

    dispatch({
      type: 'SET_USER_LOADING',
      payload: true
    });

    const companyRes = await getCompanyInfo();
    const userRes = await getUserInfo(cryptoDecrypt(Cookie.get('uID') || ''));

    dispatch({
      type: 'SET_COMPANY_INFO',
      payload: companyRes
    });

    dispatch({
      type: 'SET_USER_INFO',
      payload: userRes
    });

    dispatch({
      type: 'SET_USER_LOADING',
      payload: false
    });
  };


  // 请求公司Logo
  const onCompanyLogoLoad = async () => {
    const res = await getOperatorInfo();

    dispatch({
      type: 'SET_COMPANY_LOGO',
      payload: res.lightLogo ? `/api${res.lightLogo}?sign=${process.env.SIGN}&appid=${process.env.APPID}&timestamp=${process.env.TIMESTAMP}&_=${new Date().getTime()}` : ''
    });
  };

  // 定时请求通知消息
  useEffect(() => {
    onInfoNotifyLoad();
    infoNotifyTime.current = setInterval(() => {
      if (notifyRequestFlag.current) return;

      onInfoNotifyLoad();
    }, 30000);

    return () => {
      clearInterval(infoNotifyTime.current);
    }
  }, [])

  // 消息通知
  const onInfoNotifyLoad = async () => {
    notifyRequestFlag.current = true;
    const res = await getMessageList({
      page: 1,
      pageSize: 5,
      isRead: 0
    })

    notifyRequestFlag.current = false;
    if (!res) return;
    setInfoNotifyList(res.list);
  };

  // 消息已读
  const onMessageRead = async (id) => {
    navigate('/MessageNotify');
    await messageRead({
      id: [id],
      isAll: false
    });
    onInfoNotifyLoad();
  }

  // 组合menu
  const leftMenuListFn = (data) => {
    return data.filter(_route => {
      if (_route.link === '/networkPlatform-SmartCPEAcceleration') return process.env.ENV_TYPE !== 'dcc';

      return true;
    }).map(_menu => {
      const menuObj = {
        key: _menu.link,
        icon: <i className={`iconfont ${_menu.icon}`} />,
        label: t(_menu.title),
      };
      if (_menu.children) {
        rootSubmenuKeysRef.current.push(_menu.link)
        menuObj.children = leftMenuListFn(_menu.children)
      }

      return menuObj
    });
  };


  // 组合menu
  useEffect(() => {
    function filterMenu(menuItems) {
      return menuItems.filter((menuItem) => {
        // 检查是否存在 permissions 属性
        if (menuItem.permissions) {
          // 检查 permissions 是否存在于 permissionsArray 中
          return permissionsArrayRef?.some(_permission => _permission.includes(menuItem.permissions));
        }

        // 如果没有 permissions 属性，则保留该项
        return true;
      }).map((menuItem) => {
        // 处理子菜单项
        if (menuItem.children && menuItem.children.length > 0) {
          // 递归过滤子菜单项
          menuItem.children = filterMenu(menuItem.children);
        }

        return menuItem;
      }).filter(_menu => {
        if (_menu.children) {
          return _menu.children?.length
        }

        return true
      })
    }

    const newLeftMenu = filterMenu(leftMenu);

    setLeftMenuList(leftMenuListFn(newLeftMenu) || []);
  }, [state.userInfo.language]);


  // 初始menu高亮
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setMenuDefaultSelected(['/dashboard']);
      setPageTitle('');

      return;
    }

    if (path?.split('-')?.length === 1) {
      setMenuDefaultSelected([`/${path?.split('/')[1]}`]);
    }

    if (path?.split('-')?.length > 1) {
      setMenuDefaultSelected([path]);
      setMenuDefaultOpen([path?.split('-')[0]])
    }

  }, []);

  // 控制menu展开和高亮
  const menuOpenHighlight = (path) => {
    const pathList = path.split('-');
    let defaultSelect = [];

    pathList.forEach((item, index) => {
      if (index === 0) {
        defaultSelect.push(item);
      } else {
        let childPath = '';
        pathList.slice(0, index + 1).forEach((_child, _childIdx) => {
          childPath = _childIdx === index ? childPath + _child : childPath + _child + '-';
        })

        defaultSelect.push(childPath);
      }
    });

    // 嵌套路由时无法匹配
    defaultSelect = defaultSelect.map((item, index) => (
      index === 0 ? item : `/${item.split('/')[1]}`
    ));

    setMenuDefaultSelected(defaultSelect);
    setMenuDefaultOpen([...defaultSelect.slice(0, defaultSelect.length - 1)]);
    // setMenuDefaultOpen([...menuDefaultOpen, ...defaultSelect.slice(0, defaultSelect.length - 1)]);
  };

  // 控制顶部title
  const pageTitleShow = (data, path) => {
    data.forEach(item => {
      if (path.includes(item.link)) {
        setPageTitle(item.title);
      }
      // Dashboard不显示顶部title
      if (item.link === '/dashboard') setPageTitle('');

      item.children && pageTitleShow(item.children, path);
    })
  };

  // 控制menu高亮
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setPageTitle(mainRoutes[0].title);
      setMenuDefaultSelected(mainRoutes[0].path);

      return;
    }

    // 控制menu展开和高亮
    menuOpenHighlight(path);
    // 控制顶部title
    pageTitleShow(mainRoutes, path);
  }, [location]);

  const onSelectMenu = ({ key }) => {
    navigate(key);
  };

  const onOpenMenuChange = (keys) => {
    // rootSubmenuKeysRef
    // const latestOpenKey = keys.find((key) => menuDefaultOpen.indexOf(key) === -1);
    // if (rootSubmenuKeysRef.current.indexOf(latestOpenKey) === -1) {
    //   setMenuDefaultOpen(keys);
    // } else {
    //   setMenuDefaultOpen(latestOpenKey ? [latestOpenKey] : []);
    // }
    setMenuDefaultOpen(keys.slice(-1))
  }

  // 长时间未登录 自动退出
  const logout = () => {
    messageCom.warning(t('You have been logged out after a period of inactivity, please log in again'));

    setTimeout(() => {
      window.location.replace('/login');
      resetCookie();
    }, 600);
  };

  const resetTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = '';
    }

    // 两小时
    logoutTimerRef.current = setTimeout(logout, 7200000);
  };

  const handleUserActivity = () => {
    resetTimer();
  };

  useEffect(() => {
    resetTimer();

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);

    return () => {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = '';

      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
    };
  }, [])

  return (
    <ConfigProvider locale={locale}>
      <Layout className={styles.main_layout}>
        <Layout.Header className={styles.header_box}>
          <div className={styles.logo}>
            {/* <h1>Operator</h1> */}
            {
              state.companyLogo && (
                <img
                  style={{ height: '68%', objectFit: 'contain' }}
                  src={state.companyLogo}
                  onError={() => {
                    dispatch({
                      type: 'SET_COMPANY_LOGO',
                      payload: ''
                    })
                  }}
                />
              )
            }
          </div>
          <div className={styles.user_box}>
            <Popover
              placement="bottom"
              overlayClassName={styles.new_select_option_box}
              title={
                <span>{t('Approval Information')}</span>
              }
              showArrow={false}
              content={
                <ul className={styles.new_select_box}>
                  {
                    infoNotifyList.map(item => (
                      <li key={item.id}>
                        <div className={styles.left}>
                          <div className={styles.title}>
                            <h4 onClick={() => onMessageRead(item.id)} >{item.title}</h4>
                            {/* <span>(1)</span> */}
                          </div>
                          {/* <p dangerouslySetInnerHTML={{ __html: item.document }}></p> */}
                          <div className={styles.time}><TimeTranslateCom value={item.createTime} /></div>
                        </div>
                        {/* <div className={styles.right}>
                          <i className='iconfont icon-arrowRight' onClick={() => onMessageRead(item.id)} />
                        </div> */}
                      </li>
                    ))
                  }
                  {
                    !infoNotifyList.length && (
                      <div style={{ marginTop: 20, height: 40, textAlign: 'center' }}>
                        <span style={{ color: '#666' }}>{t('You have no latest notifications yet')}</span>
                      </div>
                    )
                  }
                  <div style={{ paddingLeft: 16 }}>
                    <span className={styles.view_more_btn} onClick={() => { navigate('/MessageNotify') }}>{t('View more')}</span>
                  </div>
                </ul>
              }
            >
              <Badge
                count={infoNotifyList.length}
                offset={[-17, 4]}
              >
                <i className='iconfont icon-xiaoxi' style={{ display: 'inline-block', marginTop: 7 }} />
              </Badge>
            </Popover>
            <Popover
              placement="bottom"
              overlayClassName={styles.lang_select_option_box}
              showArrow={false}
              content={
                <ul className={styles.lang_select_box}>
                  <li onClick={() => onLangChange('en_US')}>
                    <i className='iconfont icon-yingwen' />
                    English
                  </li>
                  <li onClick={() => onLangChange('zh_CN')}>
                    <i className='iconfont icon-jianti' />
                    简体中文
                  </li>
                  {/* <li onClick={() => onLangChange('zh_xg')}>
                    <i className='iconfont icon-fanti' />
                    繁体中文
                  </li> */}
                </ul>
              }
            >
              {
                state.userInfo.language === 'zh_CN'
                  ? <i className='iconfont icon-jianti' />
                  : state.userInfo.language === 'zh_XG'
                    ? <i className='iconfont icon-fanti' />
                    : <i className='iconfont icon-yingwen' />
              }
            </Popover>
            <HeadPortraitSelect />
          </div>
        </Layout.Header>
        <Layout className={styles.main_box}>
          <Layout.Sider
            breakpoint="lg"
            collapsible
            width={245}
            trigger={null}
            collapsed={collapsed}
            onCollapse={(collapsed) => {
              setCollapsed(collapsed);
            }}
            className={styles.left_menu_box}
          >
            <Menu
              // key={`${menuDefaultSelected}-${menuDefaultOpen}`}
              mode="inline"
              // defaultSelectedKeys={menuDefaultSelected}
              // defaultOpenKeys={menuDefaultOpen}
              selectedKeys={menuDefaultSelected}
              openKeys={menuDefaultOpen}
              onOpenChange={onOpenMenuChange}
              style={{
                // minHeight:'100%',
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}
              items={leftMenuList}
              onSelect={onSelectMenu}
            />
            <div
              className={`${styles.menu_open_icon} ${collapsed ? styles.menu_close : styles.menu_open}`}
              onClick={() => setCollapsed(!collapsed)}
            >
              {
                collapsed ?
                  <MenuUnfoldOutlined />
                  : <MenuFoldOutlined />
              }
            </div>
          </Layout.Sider>
          <Layout className={styles.content_box}>
            <div className={styles.header}>
              <h2>{t(pageTitle) || ''}</h2>
            </div>
            <Layout.Content
              className={styles.content}
            >
              <RouterViews />
            </Layout.Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider >
  )
};

export default MainLayout;
